import { GlobeIcon, MapPinIcon } from "@phosphor-icons/react";
import { memo } from "react";
import { useEnumLabel } from "@/hooks/useEnumLabel";
import { formattedDateToUTC } from "./utils";

interface ArticleHeaderProps {
  headline: string | null;
  headlineTranslation?: string | null;
  publisher?: string | null;
  publisherCountry?: string | null;
  publisherLogo?: string | null;
  datePublished?: string | null;
  url?: string | null;
}

const PublisherDisplay = memo(
  ({ logoUrl, name }: { logoUrl?: string | null; name: string }) => (
    <div className="flex items-center gap-1">
      {logoUrl && (
        <img
          src={logoUrl}
          alt={name}
          onError={(ev) => {
            (ev.target as HTMLImageElement).src = "/globe.svg";
          }}
          className="h-4 object-contain"
        />
      )}
      {!logoUrl && <GlobeIcon className="h-4 fill-gray-900" />}
      <p
        className="text-xs leading-tight font-normal text-gray-900 overflow-hidden whitespace-nowrap text-ellipsis max-w-96 md:max-w-72 lg:max-w-80 xl:max-w-96"
        title={name || "Unknown Publisher"}
      >
        {name || "Unknown Publisher"}
      </p>
    </div>
  ),
);

PublisherDisplay.displayName = "PublisherDisplay";

const ArticleHeader: React.FC<ArticleHeaderProps> = ({
  headline,
  headlineTranslation,
  publisher,
  publisherCountry,
  publisherLogo,
  datePublished,
  url,
}) => {
  const countryLabel = useEnumLabel("COUNTRY", publisherCountry);
  const displayHeadline = headlineTranslation || headline || "-";

  const renderPublisherDisplay = () => {
    if (!publisher && !publisherCountry) return null;

    return (
      <div className="flex flex-row items-center gap-x-8 text-gray-900">
        <PublisherDisplay
          logoUrl={publisherLogo}
          name={publisher || "Unknown Publisher"}
        />
        {countryLabel ? (
          <span className="flex flex-row items-center gap-x-1 text-gray-900">
            <MapPinIcon className="w-4 h-4" />
            <p className="text-xs/5">{countryLabel}</p>
          </span>
        ) : null}
      </div>
    );
  };

  const formattedDate = datePublished ? formattedDateToUTC(datePublished) : "-";

  return (
    <div className="flex min-w-0 flex-col gap-y-2 relative -mt-1">
      <div className="min-w-0">
        <p className="text-xs text-gray-800 mb-1">{formattedDate}</p>
        <a
          href={url ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xl font-serif leading-tight text-slate-700 line-clamp-3 wrap-break-word min-w-0 pr-5 hover:underline cursor-pointer"
        >
          {displayHeadline}
        </a>
      </div>
      {renderPublisherDisplay()}
    </div>
  );
};

ArticleHeader.displayName = "ArticleHeader";

export default ArticleHeader;
